import {
    Grid,
    GridColumn,
    GridColumnMenuFilter,
    GridColumnMenuSort,
    GridDataStateChangeEvent,
    GridPageChangeEvent,
    GridToolbar,
} from '@progress/kendo-react-grid'
import { AggregateDescriptor, GroupDescriptor, State } from '@progress/kendo-data-query'
import { setExpandedState } from '@progress/kendo-react-data-tools'

import { useCallback, useRef } from 'react'
import { ColumnInputFilterMenu, ColumnMenuCheckbox } from './ColumnMenu'
import {
    cellShortDate,
    cellRender,
    cellCustwindow,
    cellInvoiceWindow,
    processWithGroups,
    createAppState,
    cellCurrency,
    cellAggregateCurrency,
    rowRender,
} from './ARAgingGridFunctions'
import { CustomPager } from './CustomPager'
import { DropDownListChangeEvent } from '@progress/kendo-react-dropdowns'
import { useDashboardContext } from '../../Context/DashboardContext'
import { ExcelExport } from '@progress/kendo-react-excel-export'
import style from './GridComponentsStyle.module.scss'
import './GridTheme.scss'

const aggregates: AggregateDescriptor[] = [{ field: 'AmountDue', aggregate: 'sum' }]
const groupDescriptor: GroupDescriptor[] = [{ field: 'EntityName', aggregates: aggregates }]

const pageDataState: State = {
    group: groupDescriptor,
}
const ARAgingGrid = () => {
    const {
        dataAvailable,
        appState,
        setAppState,
        dataState,
        setDataState,
        collapsedState,
        setCollapsedState,
        operationsResult,
        setOperationsResult,
        setCurrentPage
    } = useDashboardContext()

    const _export = useRef<ExcelExport | null>(null)
    const _grid = useRef<any>()
    const excelExport = () => {
        if (_export.current !== null) {
            _export.current.save()
        }
    }

    const dataStateChange = (event: GridDataStateChangeEvent) => {
        const newDataState = processWithGroups(dataAvailable, event.dataState, aggregates)
        setOperationsResult(newDataState)
        setDataState(event.dataState)
        const skip=event.dataState.skip ? event.dataState.skip : 0
        const take=event.dataState.take ? event.dataState.take : 5
        setCurrentPage(skip/take+1)
        setAppState(
            createAppState(
                skip,
                take,
                newDataState.data
            )
        )
    }
    const pageChange = (event: GridPageChangeEvent) => {
        setAppState(createAppState(event.page.skip, event.page.take, operationsResult.data))
    }

    const handleTakeChange = (event: DropDownListChangeEvent) => {
        setCurrentPage(1)
        setAppState(createAppState(0, event.target.value, operationsResult.data))
        setCollapsedState([])
    }

    const onExpandChange = useCallback(
        (event) => {
            const item = event.dataItem
            if (item.groupId) {
                const newCollapsedIds = !event.value
                    ? [...collapsedState, item.groupId]
                    : collapsedState.filter((groupId) => groupId !== item.groupId)
                setCollapsedState(newCollapsedIds)
                const expandedData = setExpandedState({
                    data: operationsResult.data,
                    collapsedIds: newCollapsedIds,
                })
                setAppState(createAppState(appState.skip, appState.pageSize, expandedData))
            }
        },
        [appState,collapsedState]
    )

    const isColumnActive = (field: string) => {
        return GridColumnMenuFilter.active(field, dataState.filter)
    }

    const getColumnClass = (field: string): string | undefined => {
        if (
            GridColumnMenuFilter.active(field, dataState.filter) &&
            GridColumnMenuSort.active(field, dataState.sort)
        ) {
            return style.gActive
        } else if (GridColumnMenuFilter.active(field, dataState.filter)) {
            return style.filterActive
        } else if (GridColumnMenuSort.active(field, dataState.sort)) {
            return style.sortActive
        }

        return undefined
    }
    return (
        <div className="gridContainer">
            <ExcelExport
                ref={_export}
                filterable={true}
                data={[].concat(...operationsResult.data.map((obj) => obj.items))}
                fileName="PastDueReconciliation"
            >
                <Grid
                    style={{
                        height: '100%',
                    }}
                    resizable={true}
                    groupable={{ footer: 'always', enabled: false }}
                    reorderable={true}
                    sortable={true}
                    total={appState.total}
                    skip={appState.skip}
                    pageable={appState.pageable}
                    pageSize={appState.pageSize}
                    take={appState.pageSize}
                    onPageChange={pageChange}
                    data={appState.items}
                    onDataStateChange={dataStateChange}
                    {...dataState}
                    onExpandChange={onExpandChange}
                    expandField="expanded"
                    cellRender={cellRender}
                    pager={(props) => CustomPager(props, handleTakeChange)}
                    scrollable="scrollable"
                    dataItemKey={'InvoiceNo'}
                    rowRender={rowRender}
                    ref={_grid}
                >
                    <GridToolbar>
                        <button
                            title="Export Excel"
                            className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-primary"
                            onClick={excelExport}
                        >
                            Export to Excel
                        </button>
                    </GridToolbar>
                    <GridColumn
                        field="CustomerName"
                        title="Customer Name"
                        cell={cellCustwindow}
                        columnMenu={(props) =>
                            ColumnMenuCheckbox(props, dataAvailable, 'CustomerName')
                        }
                        width={'300px'}
                        headerClassName={isColumnActive('CustomerName') ? style.gActive : ''}
                        sortable={false}
                    />
                    <GridColumn
                        field="InvoiceNo"
                        title="Invoice #"
                        cell={cellInvoiceWindow}
                        columnMenu={ColumnInputFilterMenu}
                        headerClassName={getColumnClass('InvoiceNo')}
                    />
                    <GridColumn
                        field="CustomerSalesDivision"
                        title="Cust. Div"
                        columnMenu={ColumnInputFilterMenu}
                        filter={'text'}
                        width={'100px'}
                        headerClassName={
                            getColumnClass('CustomerSalesDivision')
                        }
                    />
                    <GridColumn
                        field="InvoiceDate"
                        title="Date"
                        filter="date"
                        cell={cellShortDate}
                        sortable={false}
                    />
                    <GridColumn
                        field="CustOrderNo"
                        title="Cust. Order #"
                        columnMenu={ColumnInputFilterMenu}
                        headerClassName={getColumnClass('CustOrderNo')}
                    />
                    <GridColumn
                        field="SalesOrderNo"
                        title="S/O #"
                        columnMenu={ColumnInputFilterMenu}
                        headerClassName={getColumnClass('SalesOrderNo')}
                    />
                    <GridColumn
                        field="EnteredBy"
                        title="Entered By"
                        columnMenu={(props) =>
                            ColumnMenuCheckbox(props, dataAvailable, 'EnteredBy')
                        }
                        headerClassName={getColumnClass('EnteredBy')}
                    />
                    <GridColumn
                        field="InvoiceDeliveryMethod"
                        title="Inv. Delivery Method"
                        sortable={false}
                    />
                    <GridColumn field="ProblemStatus" title="Problem Status" sortable={false} />
                    <GridColumn
                        field="LastPaymentDate"
                        title="Last Payment"
                        filter="date"
                        cell={cellShortDate}
                        sortable={false}
                    />
                    <GridColumn
                        field="InvoiceTotal"
                        title="Invoice Amount"
                        filter="numeric"
                        cell={cellCurrency}
                        sortable={false}
                    />
                    <GridColumn
                        field="AmountDue"
                        title="Amount Due"
                        filter="numeric"
                        cell={cellAggregateCurrency}
                        sortable={false}
                    />
                    <GridColumn
                        field="DaysPastDue"
                        title="Days Past Due"
                        filter="numeric"
                        width={'80px'}
                        sortable={false}
                    />
                    <GridColumn
                        field="PaidAmount"
                        title="Amount Paid"
                        filter="numeric"
                        cell={cellCurrency}
                        width={'80px'}
                        sortable={false}
                    />
                    <GridColumn
                        field="LastUpdateTimestamp"
                        title="Last Update"
                        filter="date"
                        cell={cellShortDate}
                        sortable={false}
                    />
                    <GridColumn field="LastUpdateBy" title="Last Update By" sortable={false} />
                </Grid>
            </ExcelExport>
        </div>
    )
}

export default ARAgingGrid
